import { createSlice } from '@reduxjs/toolkit';
import { Company } from '@src/types/company';
import { MetaData } from '@src/types/meta';
import { ProjectDetailInfo } from '@src/views/project/types';
import {
  completeOrderThunk,
  createCash,
  getCompanyListThunk,
  getOrderListThunk,
  receiveOrderThunk,
} from './orderHistoryThunk';
import { ModalType } from '../types';

interface ExportVoucher {
  id: number;
  name: string;
  position: string;
  email: string;
  mobileNumber: string;
  isDeposit: boolean;
  purchaseNumber: string;
}

interface BankTransfer {
  id: number;
  customerName: string;
  dueDate: string;
}

export interface OrderData {
  id: number;
  serviceId: string;
  method: string;
  amount: string;
  status: string;
  project?: { id: number; name: string; serviceId: string };
  company?: Company;
  exportVoucher?: ExportVoucher;
  bankTransfer?: BankTransfer;
  createdAt?: string;
}

interface OrderListInitialState {
  isLoading: boolean;
  modal: ModalType;
  orders: OrderData[];
  selectedOrder?: OrderData;
  getOrderParams?: {
    page?: number;
    limit?: number;
    sortBy?: string[];
    filter?: {
      status?: string;
      method?: string;
      company?: { id?: number };
    };
  };
  companyList?: { label: string; value: string }[] | null;
  getCompanyParams?: {
    page?: number;
    limit?: number;
    sortBy?: string[];
    searchBy?: string[];
    search?: string;
  };
  meta?: MetaData<{
    status?: string;
    type?: string;
    company?: {
      id?: string;
    };
    user?: {
      id?: string;
    };
  }>;
}

const orderListInitailState: OrderListInitialState = {
  isLoading: false,
  modal: 'off',
  orders: [],
  selectedOrder: {
    id: 0,
    serviceId: '',
    method: '',
    amount: '',
    status: '',
  },
  getOrderParams: {
    page: 1,
    limit: 20,
    sortBy: undefined,
    filter: {
      status: undefined,
      method: undefined,
      company: { id: undefined },
    },
  },
  companyList: [],
  getCompanyParams: {
    page: 1,
    limit: 100,
    searchBy: ['name'],
    search: '',
  },
  meta: {
    currentPage: 0,
    itemsPerPage: 20,
    sortBy: [],
    totalItems: 0,
    totalPages: 0,
  },
};

const paymentHistorySlice = createSlice({
  name: 'orderList',
  initialState: orderListInitailState,
  reducers: {
    onChangePage: (state, { payload }) => {
      state.getOrderParams.page = payload;
    },
    onChangeModal: (state, { payload }) => {
      state.modal = payload;
    },
    onSelectOrder: (state, { payload }) => {
      const tmpSeleceted = state.orders.findIndex(
        (element) => element.id === payload.id
      );

      state.selectedOrder = state.orders[tmpSeleceted];
    },

    onChangeSelecetedCompanyId: (state, { payload }) => {
      state.getOrderParams.filter.company.id = payload;
    },
    onChangeOrderMethod: (state, { payload }) => {
      state.getOrderParams.filter.method = payload;
    },
  },
  extraReducers: ({ addCase }) => {
    addCase(getOrderListThunk.pending, (state) => {
      state.isLoading = true;
    })
      .addCase(getOrderListThunk.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        const { data } = payload;
        state.meta = payload.meta;

        state.orders = data?.map(
          (element: {
            id: string;
            serviceId: string;
            method: string;
            amount: string;
            status: string;
            company: Company;
            exportVoucher: ExportVoucher;
            bankTransfer: BankTransfer;
            project: ProjectDetailInfo;
            createdAt: string;
          }) => {
            return {
              id: element.id,
              serviceId: element.serviceId,
              createdAt: element.createdAt,
              method: element.method,
              amount: element.amount,
              status: element.status,
              company: {
                id: element.company.id,
                name: element.company.name,
              },
              project: element.project
                ? {
                    id: element.project.id,
                    name: element.project.name,
                    serviceId: element.project.serviceId,
                  }
                : undefined,
              exportVoucher: element.exportVoucher,
              bankTransfer: {
                id: element.bankTransfer?.id,
                customerName: element.bankTransfer?.customerName,
                dueDate: element.bankTransfer?.dueDate,
              },
            };
          }
        );
      })
      .addCase(getOrderListThunk.rejected, (state) => {
        state.isLoading = false;
      });

    addCase(receiveOrderThunk.pending, (state) => {
      state.isLoading = true;
    })
      .addCase(receiveOrderThunk.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.selectedOrder.status = payload.status;
        const changedIdx = state.orders.findIndex(
          (element) => element.id === payload.id
        );
        const tmpOrder = {
          id: payload.id,
          serviceId: payload.serviceId,
          method: payload.method,
          amount: payload.amount,
          status: payload.status,
          company: payload.company,
          exportVoucher: payload.exportVoucher,
          createdAt: payload.createdAt,
        };
        state.orders[changedIdx] = tmpOrder;
      })
      .addCase(receiveOrderThunk.rejected, (state) => {
        state.isLoading = false;
      });

    addCase(completeOrderThunk.pending, (state) => {
      state.isLoading = true;
    })
      .addCase(completeOrderThunk.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.selectedOrder.status = payload.status;
        const changedIdx = state.orders.findIndex(
          (element) => element.id === payload.id
        );
        const tmpOrder = {
          id: payload.id,
          serviceId: payload.serviceId,
          method: payload.method,
          amount: payload.amount,
          status: payload.status,
          company: payload.company,
          exportVoucher: payload.exportVoucher,
          createdAt: payload.createdAt,
        };
        state.orders[changedIdx] = tmpOrder;
      })
      .addCase(completeOrderThunk.rejected, (state) => {
        state.isLoading = false;
      });

    addCase(getCompanyListThunk.fulfilled, (state, { payload }) => {
      const tmpData = payload.map((element: { name: string; id: number }) => {
        return { label: element.name, value: element.id };
      });
      state.companyList = tmpData;
    });

    addCase(createCash.rejected, (_, { payload }) => {
      throw payload;
    });
  },
});

export const {
  onChangePage,
  onChangeModal,
  onSelectOrder,
  onChangeSelecetedCompanyId,
  onChangeOrderMethod,
} = paymentHistorySlice.actions;

export default paymentHistorySlice.reducer;
